import React, { useState } from 'react';
import { flex } from '@fabric-ui/theme';
import { Box, Divider, InputLabel, Slider, Typography } from '@mui/material';
import { SEVERITY_LABELS, SeverityIcons, SeverityLabels, SeverityPrimaryColors, TextInput } from './';

const marks = [
  { value: 0.1, label: '0.1' },
  { value: 2, label: '2' },
  { value: 4, label: '4' },
  { value: 6, label: '6' },
  { value: 8, label: '8' },
  { value: 9.9, label: '10' },
];

const severityConfig: { label: string; upperThreshold?: number }[] = [
  { label: 'INFO', upperThreshold: 2 },
  { label: 'LOW', upperThreshold: 4 },
  { label: 'MEDIUM', upperThreshold: 6 },
  { label: 'HIGH', upperThreshold: 8 },
  { label: 'CRITICAL' },
];

const calculateSeverity = (value: number) =>
  severityConfig.find(cur => (cur.upperThreshold ? value <= cur.upperThreshold : true))?.label || 'CRITICAL';

type AvSeveritySliderProps = {
  value: number;
  onChange: (severity_score: number, severity: string) => void;
};

export const AvSeveritySlider = ({ value, onChange }: AvSeveritySliderProps) => {
  const [error, setError] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSliderChange = newValue => onChange(newValue, calculateSeverity(newValue).toUpperCase());

  const handleInputChange = num => {
    if (num > 10 || num < 0) {
      setError('Number should be between 0 and 10');
    } else {
      onChange(num, calculateSeverity(num));
      setError('');
    }
  };
  return (
    <div>
      <InputLabel required>Severity</InputLabel>
      <Box sx={{ ...flex.itemsCenter, gap: '20px' }}>
        <Slider
          sx={{
            '.MuiSlider-rail': {
              background: `linear-gradient(90deg,
                 ${SeverityPrimaryColors[SeverityLabels.Info]} 0%, ${SeverityPrimaryColors[SeverityLabels.Info]} 20%,
                 ${SeverityPrimaryColors[SeverityLabels.Low]} 20%, ${SeverityPrimaryColors[SeverityLabels.Low]} 40%, ${SeverityPrimaryColors[SeverityLabels.Medium]} 40%,
                 ${SeverityPrimaryColors[SeverityLabels.Medium]} 60%, ${SeverityPrimaryColors[SeverityLabels.High]} 60%, ${SeverityPrimaryColors[SeverityLabels.High]} 80%, 
                 ${SeverityPrimaryColors[SeverityLabels.Critical]} 80%, ${SeverityPrimaryColors[SeverityLabels.Critical]} 100% )`,
              opacity: '1',
            },

            '.MuiSlider-track': {
              backgroundColor: 'transparent',
              borderWidth: 0,
            },

            height: 6,
            width: 390,
            marginTop: '2px',
          }}
          value={value}
          onChange={(e, value) => handleSliderChange(value)}
          min={0}
          max={10}
          step={0.1}
          marks={marks}
          valueLabelDisplay="auto"
        />
        <TextInput
          value={isFocused ? value : value === null ? '0.0' : value.toFixed(1)}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          variant="outlined"
          size="small"
          sx={{
            width: 150,
            mb: '18px',
            '& .MuiInputBase-input': {
              lineHeight: 1,
              fontWeight: 600,
            },
            '.MuiOutlinedInput-root.MuiInputBase-root': {
              pr: 1,
              pl: '10px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-border-severity-none-secondary)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-border-severity-none-secondary)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--color-border-interactive-primary-default)',
            },
          }}
          type="number"
          error={error}
          inputProps={{
            endAdornment: (
              <Box sx={{ width: 100, ...flex.itemsCenter }}>
                <Divider orientation="vertical" flexItem sx={{ height: 20, marginTop: '3px' }} />
                <Box sx={{ width: 94, pl: 1, ...flex.itemsCenter, gap: '4px' }}>
                  {SeverityIcons[calculateSeverity(value).toUpperCase()].icon}
                  <Typography variant="body2">{SEVERITY_LABELS[calculateSeverity(value)]} </Typography>
                </Box>
              </Box>
            ),
          }}
        />
      </Box>
    </div>
  );
};
